import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  IconButton,
  Typography,
  useTheme,
  TextField,
  Autocomplete,
} from "@mui/material";
import Tabs, { tabsClasses } from "@mui/material/Tabs";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import { Card, TableContainer, Button, TableHead } from "@mui/material";
import { styled } from "@mui/material";
import {
  requestMarketData,
  setCalculatedMonitorScreen,
} from "../../Redux/Reducers/marketDataSlice";
import {
  TABLE_HEAD_VANTAGE as TABLE_HEAD,
  applySortFilter,
  calculateAndFormat,
  getComparator,
  StyledTab,
} from "../../component/Methods";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import MonitorScreenCalculation from "../Portfolio/MonitorScreenCalculation";
import VantageListHead from "../../component/ListHead/VantageListHead";
import PerformanceFundPopUp from "./Popup/PerformanceFundPopUp";
import FundStrategyPopUp from "./Popup/FundStrategyPopUp";
import { StrategiesDashboard } from "../../component/Methods/dashboardCalculations";
import PositionPeformance from "./Popup/PositionPerformance";
import BenchMarkTable from "./BenchMarkTable";
import Switch from "@mui/material/Switch";
import Loader from "../../component/Loader";
import { getMarketDataThreshold } from "../../Redux/Reducers/selectors";
import { useVantageSystemState } from "../../hooks/useOptimizedState";
import {
  isValidNum,
  getSignColor,
  formatCurrency,
  formatPercentPlain,
  formatPercentSigned,
  formatLongCapital,
  formatShortCapital,
  formatNetCapital,
  formatPercentColored,
} from "./utility";

const label = { inputProps: { "aria-label": "Switch demo" } };

const Tablecell = styled(TableCell)(({ theme }) => ({
  fontSize: "12px",
  padding: "1px 2px 1px 5px",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor:
      theme.palette.mode === "light"
        ? "rgba(0, 0, 0, 0.04)"
        : "rgba(0, 0, 0, 0.15)",
  },
  "&:nth-of-type(odd)": {
    backgroundColor: "transparent",
  },
  "&:hover": {
    backgroundColor:
      theme.palette.mode === "light"
        ? "rgba(255, 255, 255, 0.7)"
        : "rgba(255, 255, 255, 0.08)",
  },
}));

const PerformanceDetails = () => {
  const threshold = useSelector(getMarketDataThreshold);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [monitorScreenValuesObj, setMonitorScreenValuesObj] = useState([]);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("aum");
  const [filterName, setFilterName] = useState("");
  const [monitor, setMonitor] = useState([]);
  const [isGridViewPositions, setIsGridViewPositions] = useState(false);
  const [isGridViewPerformance, setIsGridViewPerformance] = useState(false);
  const [isPositionPerformance, setIsPositionPerformance] = useState(false);
  const [checked, setChecked] = useState(true);
  const [value, setValue] = React.useState(0);
  const [isMarketData, setIsMarketData] = useState(false);
  const [dataFetching, setDataFetching] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);

  // Keep the optimized Redux hook - this is the most important optimization
  const {
    funds,
    strategies,
    trades,
    lots,
    livePrices,
    markets,
    marketData,
    RawMarketData,
    accountingData,
    isStrategist,
  } = useVantageSystemState();

  // Simple calculation - no memoization needed for simple functions
  const resultArray = StrategiesDashboard();
  useEffect(() => {
    const filteredData = MonitorScreenCalculation({
      selectedFund: undefined,
      selectedStrategy: undefined,
      selectedSide: undefined,
      lots: lots,
      accountingData: accountingData,
      funds: funds,
      strategies: strategies,
      marketData: livePrices,
      trades: trades,
      isStrategist: isStrategist,
      markets: markets,
    });
    if (filteredData) dispatch(setCalculatedMonitorScreen(filteredData));
  }, [
    lots,
    accountingData,
    funds,
    strategies,
    livePrices,
    trades,
    isStrategist,
    markets,
  ]);

  const timeoutRef = useRef(null);
  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
      return;
    }

    // Clear the previous timeout if it exists
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setDataFetching(true);
    // Set a new timeout to trigger the function after 20 seconds
    timeoutRef.current = setTimeout(() => {
      setIsMarketData(false);
      dispatch(
        requestMarketData({
          lots: lots,
          threshold: threshold,
          noRequest: true,
          trades: trades,
        })
      );
    }, 20000); // 10 seconds

    // Cleanup the timeout if the component unmounts or RawMarketData changes
    return () => clearTimeout(timeoutRef.current);
  }, [RawMarketData]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };
  const handleChangeCheck = (event) => {
    setChecked(event.target.checked);
  };

  // Memoize expensive calculation - this is safe and provides good performance benefit
  const calculatedValues = useMemo(
    () =>
      MonitorScreenCalculation({
        lots: lots,
        accountingData: accountingData,
        funds: funds,
        strategies: strategies,
        marketData: livePrices,
        markets: markets,
      }),
    [lots, accountingData, funds, strategies, livePrices, markets]
  );
  const findFundAum = (key, array) => {
    if (key && array && array[0]?.funds) {
      const findAum = array[0]?.funds?.find((i) => i.fund === key);
      return findAum?.aum;
    }
  };

  // Memoize expensive accFund calculation
  const accFund = useMemo(() => {
    if (!funds) return [];

    const findFunds = funds?.filter(
      (i) =>
        i.abbreviation === "wsf" ||
        i.abbreviation === "pmp" ||
        i.abbreviation === "top" ||
        i.abbreviation === "dbbs" ||
        i.abbreviation === "ral1" ||
        i.abbreviation === "ral2" ||
        i.abbreviation === "jk" ||
        i.abbreviation === "sthw" ||
        i.abbreviation === "sche" ||
        i.abbreviation === "spir"
    );

    const transformedFunds = findFunds?.map((fund) => {
      const calc = MonitorScreenCalculation({
        selectedFund: fund.fundId,
        lots: lots,
        accountingData: accountingData,
        funds: funds,
        strategies: strategies,
        marketData: livePrices,
        markets: markets,
      });


      const totalPLLong = calc.reduce((total, obj) => {
        return obj.deltaAdjustedShareCount > 0
          ? total + parseFloat(obj.pL)
          : total;
      }, 0);
      const totalPLShort = calc.reduce((total, obj) => {
        return obj.deltaAdjustedShareCount < 0
          ? total + parseFloat(obj.pL)
          : total;
      }, 0);

      const totalPL = totalPLLong + totalPLShort;

      return {
        name: fund.name,
        aum: findFundAum(fund.fundId, accountingData),
        size: totalPL,
        pf:
          (totalPL / parseFloat(findFundAum(fund.fundId, accountingData))) *
          100,
      };
    });

    return transformedFunds;
  }, [
    funds,
    accountingData,
    marketData,
    lots,
    strategies,
    livePrices,
    markets,
  ]);

  // Memoize filtered table data
  const filteredTableData = useMemo(() => {
    return monitorScreenValuesObj?.filter((row) => {
      const meetsAumCondition =
        (checked && parseFloat(row?.aum) > 10) || !checked;
      const meetsFundCondition =
        !selectedFund || row.fundName === selectedFund.label;
      return meetsAumCondition && meetsFundCondition;
    });
  }, [monitorScreenValuesObj, checked, selectedFund]);

  // Memoize fund names for autocomplete
  const getUniqueFundNames = useMemo(() => {
    const fundNames = new Set();
    monitorScreenValuesObj?.forEach((row) => {
      if (row.fundName) {
        fundNames.add(row.fundName);
      }
    });
    return Array.from(fundNames).map((name) => ({ label: name }));
  }, [monitorScreenValuesObj]);

  // Memoize static array
  const positions = useMemo(() => ["Fund", "AUM", "P(L)", "P(L)%"], []);

  // Optimize event handlers with useCallback
  const handleCellClick = useCallback(
    (fundName) => {
      navigate(`/portfolio/${fundName}`);
    },
    [navigate]
  );

  const handleCellClickStrategy = useCallback(
    (fundName, strategyName) => {
      navigate(`/portfolio/${fundName}/${strategyName}`);
    },
    [navigate]
  );

  const handleRequestSort = useCallback(
    (event, property) => {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    },
    [orderBy, order]
  );

  const toggleGridViewPositions = useCallback(() => {
    setIsGridViewPositions((prev) => !prev);
  }, []);

  const toggleGridViewPerformance = useCallback(() => {
    setIsGridViewPerformance((prev) => !prev);
  }, []);

  const handleMarketData = useCallback(() => {
    setIsMarketData(true);
    setDataFetching(false);

    dispatch(
      requestMarketData({ lots: lots, threshold: threshold, trades: trades })
    );

    setTimeout(() => {
      if (!dataFetching) {
        setIsMarketData(false);
      }
    }, 20000);
  }, [dispatch, lots, threshold, trades, dataFetching]);
  useEffect(() => {
    setMonitor(resultArray);
    const filteredUsers = applySortFilter(
      resultArray,
      getComparator(order, orderBy),
      filterName
    );
    setMonitorScreenValuesObj(filteredUsers);
  }, [order, orderBy, filterName, calculatedValues]);

  const theme = useTheme();

  return isMarketData ? (
    <Loader msg={true} marketLoader={true} />
  ) : (
    <>
      <Box
        sx={{
          // Adjust this value based on header height + padding
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            mb: 0,
          }}
        >
          <Box sx={{ width: "50%", marginRight: 0, padding: "0px" }}>
            {" "}
            <Box
              sx={{
                border: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                borderRadius: "4px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: theme.palette.lightGrey.tableHeader,
                  px: 1,
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ color: theme.palette.lightGrey.textColor }}
                >
                  Longs and Shorts Summary
                </Typography>
                <IconButton
                  onClick={toggleGridViewPositions}
                  aria-label="Toggle GridView"
                  sx={{ height: "20px", width: "20px" }}
                >
                  <FullscreenIcon sx={{ height: "15px", width: "15px" }} />
                </IconButton>
              </Box>
              <Table aria-label="a dense table" size="small">
                <TableHead>
                  <TableRow>
                    {positions?.map((i, index) => {
                      return (
                        <Tablecell
                          align="center"
                          key={index}
                          sx={{
                            borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                            color: theme.palette.lightGrey.textColor,
                            fontSize: theme.typography.bodyText12.fontSize,
                            fontWeight: theme.typography.bodyText12.fontWeight,
                          }}
                        >
                          {i}
                        </Tablecell>
                      );
                    })}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {accFund?.map((row, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        background: theme.palette.grey[100],
                        "&:hover": {
                          background: theme.palette.grey[200],
                          cursor: "pointer",
                        },
                      }}
                    >
                      <Tablecell
                        align="left"
                        sx={{
                          borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                        }}
                      >
                        {row?.name}
                      </Tablecell>
                      <Tablecell
                        sx={{
                          borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                        }}
                        align="right"
                      >
                        {isValidNum(row?.aum)
                          ? calculateAndFormat(row?.aum)
                          : ""}
                      </Tablecell>
                      <Tablecell
                        style={{
                          borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                          color: getSignColor(row?.size, theme),
                        }}
                        align="right"
                      >
                        {formatCurrency(row?.size)}
                      </Tablecell>
                      <Tablecell
                        style={{
                          borderRight: `1px solid ${theme.palette.lightGrey.tableHeader}`,
                          color: getSignColor(row?.pf, theme),
                        }}
                        align="right"
                      >
                        {formatPercentColored(row?.pf)}
                      </Tablecell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <Tabs
              value={value}
              onChange={handleChange}
              allowScrollButtonsMobile
              aria-label="scrollable force tabs example"
              sx={{
                [`& .${tabsClasses.scrollButtons}`]: {
                  "&.Mui-disabled": { opacity: 0.3 },
                  borderRadius: "50%",
                  background: "#808080",
                  height: "20px",
                  width: "20px",
                  mt: 2,
                },
              }}
              indicatorColor="white"
              textColor="inherit"
            >
              {/* <StyledTab
                    onClick={() => {
                      setIsPositionPerformance(true);
                    }}
                    label="Positions Performance"
                  /> */}
            </Tabs>
          </Box>
          <Box sx={{ width: "50%", marginRight: 0, padding: "0px" }}>
            <BenchMarkTable marketData={livePrices} />
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            mt: -2,
          }}
        >
          <Box sx={{ margin: '4px 0 15px', flexShrink: 0 }}>
            <Autocomplete
              size="small"
              variant="outlined"
              options={getUniqueFundNames}
              value={selectedFund}
              onChange={(event, newValue) => {
                setSelectedFund(newValue);
              }}
              sx={{ width: 200 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Fund"
                  variant="outlined"
                  size="small"
                />
              )}
            />
          </Box>

          <Box
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: "hidden",
              flex: 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <TableContainer sx={{ flex: 1, overflow: "auto" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: theme.palette.lightGrey.tableHeader,
                  px: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: theme.palette.lightGrey.textColor }}
                  >
                    Funds and Strategies Performance
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ color: theme.palette.lightGrey.textColor }}
                  >
                    Hide strategies with zero AUM
                  </Typography>
                  <Switch
                    size="small"
                    checked={checked}
                    onChange={handleChangeCheck}
                    inputProps={{ "aria-label": "controlled" }}
                  />

                  {/* <IconButton
                    onClick={toggleGridViewPerformance}
                    aria-label="Toggle GridView"
                    sx={{ height: "20px", width: "20px" }}
                  >
                    <FullscreenIcon sx={{ height: "15px", width: "15px" }} />
                  </IconButton> */}
                </Box>
              </Box>

              <Table
                size="small"
                stickyHeader
                aria-label="a dense table"
                sx={{
                  height: "max-content",
                  borderCllapse: "collapse",
                  border: "solid 1px #eee",
                }}
              >
                <VantageListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  noStatus={true}
                  onRequestSort={handleRequestSort}
                />

                <TableBody>
                  {filteredTableData?.map((row, i) => (
                    <StyledTableRow rowheight={0} key={i}>
                      <Tablecell
                        align="left"
                        onClick={() => handleCellClick(row.fundName)}
                        sx={{
                          cursor: "pointer",
                          fontWeight: "500",
                          paddingLeft: "5px",
                        }}
                      >
                        {row.fundName}
                      </Tablecell>
                      <Tablecell
                        align="left"
                        onClick={() =>
                          row.strategyName == "CPFactorBW"
                            ? handleCellClick(row.fundName)
                            : handleCellClickStrategy(
                              row.fundName,
                              row.strategyName
                            )
                        }
                        sx={{
                          cursor: "pointer",
                          paddingLeft: "5px",
                          width: "10",
                        }}
                      >
                        {row.strategyName}
                      </Tablecell>
                      <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                        {formatCurrency(row?.aum)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.pL, theme),
                        }}
                      >
                        {formatCurrency(row?.pL)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.plLong, theme),
                        }}
                      >
                        {formatCurrency(row?.plLong)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.plShort, theme),
                        }}
                      >
                        {formatCurrency(row?.plShort)}
                      </Tablecell>
                      <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                        {formatPercentPlain(row?.sizeLong)}
                      </Tablecell>
                      <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                        {formatPercentSigned(row?.sizeShort)}
                      </Tablecell>
                      <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                        {formatPercentSigned(row?.netAumPercent)}
                      </Tablecell>
                      <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                        {formatLongCapital(row?.longCapital)}
                      </Tablecell>
                      <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                        {formatShortCapital(row?.shortCapital)}
                      </Tablecell>
                      <Tablecell align="right" sx={{ paddingRight: "5px" }}>
                        {formatNetCapital(row?.netCapital)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.longPerformance, theme),
                        }}
                      >
                        {formatPercentColored(row?.longPerformance)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.shortPerformance, theme, true),
                        }}
                      >
                        {formatPercentColored(row?.shortPerformance)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.pLYest, theme),
                        }}
                      >
                        {formatPercentColored(row?.pLYest)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.plPer, theme),
                        }}
                      >
                        {formatPercentColored(row?.plPer)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.pLMonth, theme),
                        }}
                      >
                        {row.strategyName == "CPFactorBW" ? "-" : formatPercentColored(row?.pLMonth)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.pLQuarter, theme),
                        }}
                      >
                        {row.strategyName == "CPFactorBW" ? "-" : formatPercentColored(row?.pLQuarter)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.pLYear, theme),
                        }}
                      >
                        {row.strategyName == "CPFactorBW" ? "-" : formatPercentColored(row?.pLYear)}
                      </Tablecell>
                      <Tablecell
                        align="right"
                        style={{
                          paddingRight: "5px",
                          color: getSignColor(row?.pLITD, theme),
                        }}
                      >
                        {row.strategyName == "CPFactorBW" ? "-" : formatPercentColored(row?.pLITD)}
                      </Tablecell>

                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      </Box>

      <PositionPeformance
        title="POSITIONS PERFORMANCE"
        isPositionPerformance={isPositionPerformance}
        setIsPositionPerformance={setIsPositionPerformance}
      />

      <PerformanceFundPopUp
        openPositionsPopup={isGridViewPositions}
        setopenPositionsPopup={setIsGridViewPositions}
        accFund={accFund}
        tablehead={positions}
        title="Longs and Shorts Summary"
      />
      <FundStrategyPopUp
        openPerformancePopup={isGridViewPerformance}
        setopenPerformancePopup={setIsGridViewPerformance}
        checked={checked}
        monitor={filteredTableData}
        title="Funds and Strategies Performance"
      />
    </>
  );
};
export default PerformanceDetails;
